'use client'

import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import cn from 'classnames';
import { ImageProps } from 'next/image';
import { useEffect, useRef, useState } from 'react';
import React from 'react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import Image from '@/app/_components/Image';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import PauseIconDisable from '@/app/_icons/pause-gray.svg';
import PauseIcon from '@/app/_icons/pause.svg';
import PlayIconDisable from '@/app/_icons/play-gray.svg';
import PlayIcon from '@/app/_icons/play.svg';
import { PriceDisplayModes } from '@/constants/new-cars/new-cars.constants';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { directusApiUri } from '@/services/api.service';
import { getImageLink } from '@/services/link.service';
import { BannerCarousel } from '@/types/banners/banners.types';
import { NewCarData } from '@/types/new-cars/new-cars.types';
import imageLoader from '@/utils/image-loaders';

import Styles from './Banner.module.scss';

type BannerProps = {
  priceFrom?: string | number;
  model?: string;
  align?: string;
  banner: BannerCarousel;
  newVehicle: NewCarData;
  showOverlay?: boolean;
};

const BannerSection = ({
  align = 'left',
  priceFrom,
  model,
  newVehicle,
  banner,
  showOverlay = true,
}: BannerProps) => {
  const video = getImageLink(banner?.video);
  const [playing, setPlaying] = useState(true);
  const desktopVideoRef = useRef<HTMLVideoElement | null>(null);
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null);
  const { openLeadForm } = useFloatingCTA();

  const videoHandler = (control: string) => {
    const videoElement =
      window.innerWidth >= 768
        ? desktopVideoRef.current
        : mobileVideoRef.current;

    if (!videoElement) {
      return;
    }

    if (control === 'play') {
      videoElement.play();
      setPlaying(true);
    } else if (control === 'pause') {
      videoElement.pause();
      setPlaying(false);
    }
  };

  useEffect(() => {
    if (!video) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const videoElement =
        window.innerWidth >= 768
          ? desktopVideoRef.current
          : mobileVideoRef.current;

      if (!videoElement || videoElement.src) {
        return;
      }

      videoElement.src = video;
      videoElement.load();

      videoElement.play().catch(() => {});
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [video]);

  const imageProps: ImageProps = {
    src: `${directusApiUri}/assets/${banner?.desktop_image}?__dis=${banner?.desktop_image}&__mis=${banner?.mobile_image}`,
    alt: banner?.desktop_image,
    placeholder: 'empty',
    priority: true,
    quality: 75,
    loader: imageLoader,
    id: banner?.mobile_image,
    sizes: '(max-width: 575px) 30vw',
  };

  return (
    <Box
      className={cn(
        Styles.bannerWrapper,
        {
          [Styles.topAlign as string]: align === 'top',
          [Styles.leftAlign  as string]: align === 'left',
          [Styles.rightAlign  as string]: align === 'right',
          [Styles.centerAlign  as string]: align === 'centre_bottom',
        },
        showOverlay && Styles.withOverlay
      )}
    >
      <Box
        className={Styles.bannerSectionWrapper}
        paddingTop={8}
        paddingBottom={12}
        paddingX={6}
      >
        <Box className={Styles.headingElement} justifyContent={'center'}>
          {banner?.heading &&
            <Heading
              as="h2"
              className={Styles.titleXl}
              color={'white'}
              marginBottom={6}
              textAlign={'center'}
              textTransform={'capitalize'}
            >
              {banner?.heading}
            </Heading>
          }
        </Box>

        {banner?.subheading && (
          <Text
            as="h2"
            className="titleXs"
            color="white"
            marginBottom={6}
            textAlign={'center'}
            textTransform="capitalize"
          >
            {banner.subheading}
          </Text>
        )}

        {priceFrom && (
          <Stack gap={1} width={'max-content'} marginX={'auto'}>
            <Flex
              gap={{ base: '2' }}
              marginBottom={6}
              justifyContent={'center'}
            >
              <Text color={'white'} marginTop={'6px'}>
                From
              </Text>
              <Heading
                as="h2"
                className={Styles.titleMd}
                fontSize={{ base: '2xl', sm: '4xl' }}
                color={'white'}
              >
                {priceFrom !== undefined && (
                  <PriceFormat value={priceFrom} prefix={'R'} />
                )}
              </Heading>
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                Incl VAT
              </Text>
            </Flex>
          </Stack>
        )}

        {newVehicle?.price_display_mode === PriceDisplayModes.BOTH &&
        newVehicle?.price_from &&
        newVehicle?.price_per_month_from ? (
          <Stack gap={1} width={'max-content'} marginX={'auto'}>
            <Flex
              gap={{ base: '2' }}
              marginBottom={6}
              justifyContent={'center'}
            >
              <Text color={'white'} marginTop={'6px'}>
                From
              </Text>
              <Heading
                as="h2"
                className={Styles.titleMd}
                fontSize={{ base: '2xl', sm: '4xl' }}
                color={'white'}
              >
                {newVehicle?.price_from !== undefined && (
                  <PriceFormat value={newVehicle?.price_from} prefix={'R'} />
                )}
              </Heading>
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                Incl VAT,
              </Text>
            </Flex>
            <Flex
              gap={{ base: '2' }}
              marginBottom={6}
              justifyContent={'center'}
            >
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                or as little as
              </Text>
              <Heading
                as="h2"
                className={Styles.titleMd}
                fontSize={{ base: '2xl', sm: '4xl' }}
                color={'white'}
              >
                <PriceFormat
                  value={newVehicle?.price_per_month_from}
                  prefix="R"
                />
              </Heading>
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                per month *
              </Text>
            </Flex>
          </Stack>
        ) : newVehicle?.price_display_mode === PriceDisplayModes.RETAIL &&
          newVehicle?.price_from ? (
          <Stack gap={1} width={'max-content'} marginX={'auto'}>
            <Flex
              gap={{ base: '2' }}
              marginBottom={6}
              justifyContent={'center'}
            >
              <Text color={'white'} marginTop={'6px'}>
                From
              </Text>
              <Heading
                as="h2"
                className={Styles.titleMd}
                fontSize={{ base: '2xl', sm: '4xl' }}
                color={'white'}
              >
                {newVehicle?.price_from !== undefined && (
                  <PriceFormat value={newVehicle?.price_from} prefix={'R'} />
                )}
              </Heading>
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                Incl VAT
              </Text>
            </Flex>
          </Stack>
        ) : newVehicle?.price_per_month_from ? (
          <Stack gap={1} width={'max-content'} marginX={'auto'}>
            <Flex
              gap={{ base: '2' }}
              marginBottom={6}
              justifyContent={'center'}
            >
              <Text color={'white'} marginTop={'6px'}>
                From
              </Text>
              <Heading
                as="h2"
                className={Styles.titleMd}
                fontSize={{ base: '2xl', sm: '4xl' }}
                color={'white'}
              >
                {newVehicle?.price_per_month_from !== undefined && (
                  <PriceFormat
                    value={newVehicle?.price_per_month_from}
                    prefix={'R'}
                  />
                )}
              </Heading>
              <Text color={'white'} alignSelf={'flex-end'} marginBottom={1}>
                per month *
              </Text>
            </Flex>
          </Stack>
        ) : null}

        <Flex
          gap={4}
          width={'max-content'}
          marginX={'auto'}
          direction={'column'}
          justifyContent={'center'}
        >
          {banner?.show_book_test_drive && (
            <ButtonCustom
              btnText={banner?.book_test_drive_label || 'Book a Test Drive'}
              className="btnWhite"
              moduleClass={Styles.bannerCta}
              onClick={() =>
                openLeadForm('test-drive', 'TestDrive', {
                  type: 'new-car',
                  newCar: newVehicle,
                })
              }
            />
          )}
          {banner?.source_code && (
            <ButtonCustom
              btnText="Apply for Finance"
              className="btnWhite"
              moduleClass={Styles.bannerCta}
              onClick={() =>
                openLeadForm('finance', 'finance', {
                  type: 'new-car',
                  newCar: newVehicle,
                })
              }
            />
          )}
          {banner?.show_call_to_action &&
            banner?.call_to_action_url &&
            banner?.call_to_action_text && (
              <LinkCustom
                href={banner?.call_to_action_url}
                className="btnOutlineWhite"
                moduleClass={Styles.bannerCta}
              >
                {banner?.call_to_action_text}
              </LinkCustom>
            )}
          {banner?.show_call_to_action_2 &&
            banner?.call_to_action_url_2 &&
            banner?.call_to_action_text_2 && (
              <LinkCustom
                href={banner?.call_to_action_url_2 ?? '#'}
                className="btnWhite"
                moduleClass={Styles.bannerCta}
              >
                {banner?.call_to_action_text_2}
              </LinkCustom>
            )}
        </Flex>

        <Box textAlign={'center'} color={'white'} marginTop={'10px'}>
          <Text>{banner?.terms_and_conditions}</Text>
        </Box>
      </Box>

      <div className={Styles.heroSwiper}>
          <Box>
            <Box
              className={cn(
                Styles.heroSwiperWrap,
                video && Styles.heroSwiperWrapVideo,
              )}
            >
              {(banner?.desktop_image || banner?.mobile_image) && (
                <Box className={Styles.bannerImage}>
                  <Image
                    {...imageProps}
                    alt={`kia-${model}`}
                    title={`kia-${model}`}
                    width={1440}
                    height={817}
                    style={{ objectFit: 'cover'}}
                  />
                </Box>
              )}

              {video && (
                <Box className={Styles.slideDesktop}>
                  <Box className={Styles.videoPlayer}>
                    <video
                      playsInline
                      ref={desktopVideoRef}
                      className={Styles.videoPlayerFrame}
                      preload="none"
                      autoPlay
                      muted
                      loop
                    />
                  </Box>
                </Box>
              )}

              {/* Play in mobile */}
              {!banner?.mobile_image && !banner?.desktop_image && video && (
                <Box className={Styles.videoPlayer}>
                  <Box className={Styles.videoPlayerController}>
                    <Stack gap={2.5} marginEnd={2}>
                      {playing ? (
                        <Flex
                          alignItems={'center'}
                          onClick={() => videoHandler('pause')}
                          cursor={'pointer'}
                        >
                          <Image
                            alt="play"
                            title="play"
                            height={16}
                            width={16}
                            src={PlayIconDisable}
                          />
                          <Image
                            alt="pause"
                            title="pause"
                            height={16}
                            width={16}
                            src={PauseIcon}
                          />
                          <Text
                            as="b"
                            lineHeight={'1'}
                            mt={0.5}
                            fontSize="xs"
                            color={'white'}
                          >
                            PAUSE
                          </Text>
                        </Flex>
                      ) : (
                        <Flex
                          alignItems={'center'}
                          onClick={() => videoHandler('play')}
                          cursor={'pointer'}
                        >
                          <Image
                            alt="play"
                            title="play"
                            height={16}
                            width={16}
                            src={PlayIcon}
                          />
                          <Image
                            alt="pause"
                            title="pause"
                            height={16}
                            width={16}
                            src={PauseIconDisable}
                          />
                          <Text
                            as="b"
                            lineHeight={'1'}
                            mt={0.5}
                            fontSize="xs"
                            color={'white'}
                          >
                            PLAY
                          </Text>
                        </Flex>
                      )}
                    </Stack>
                  </Box>
                  <video
                    playsInline
                    ref={mobileVideoRef}
                    className={Styles.videoPlayerFrame}
                    preload="none"
                    autoPlay
                    muted
                    loop
                  />
                </Box>
              )}

              <Container
                className={Styles.heroBannerContentWrapper}
                maxW={'1280'}
                width={'100%'}
                zIndex={4}
              >
                <Box className={Styles.slideDesktop}>
                  <Box className={Styles.heroBannerContent}>
                    <Box className={Styles.headingElement}>
                      <Heading
                        as="h1"
                        className={Styles.titleXl}
                        color={'white'}
                        marginBottom={6}
                        textTransform={'capitalize'}
                      >
                        {banner?.heading}
                      </Heading>
                      {banner?.subheading && (
                        <Text
                          as="h2"
                          className="titleXs"
                          color="white"
                          marginBottom={6}
                          textAlign={'center'}
                          textTransform="capitalize"
                        >
                          {banner.subheading}
                        </Text>
                      )}
                    </Box>

                    {priceFrom && (
                      <Flex
                        gap={{ base: '2' }}
                        marginBottom={6}
                        justifyContent={'center'}
                      >
                        <Text color={'white'} marginTop={'6px'}>
                          From
                        </Text>
                        <Heading
                          as="h2"
                          className={Styles.titleMd}
                          fontSize={{ base: '2xl', sm: '4xl' }}
                          color={'white'}
                        >
                          {priceFrom !== undefined && (
                            <PriceFormat value={priceFrom} prefix={'R'} />
                          )}
                        </Heading>
                        <Text
                          color={'white'}
                          alignSelf={'flex-end'}
                          marginBottom={1}
                        >
                          Incl VAT
                        </Text>
                      </Flex>
                    )}

                    {newVehicle?.price_display_mode ===
                      PriceDisplayModes.BOTH &&
                    newVehicle?.price_from &&
                    newVehicle?.price_per_month_from ? (
                      <>
                        <Flex
                          gap={{ base: '2' }}
                          marginBottom={6}
                          justifyContent={'center'}
                        >
                          <Text color={'white'} marginTop={'6px'}>
                            From
                          </Text>
                          <Heading
                            as="h2"
                            className={Styles.titleMd}
                            fontSize={{ base: '2xl', sm: '4xl' }}
                            color={'white'}
                          >
                            {newVehicle?.price_from !== undefined && (
                              <PriceFormat
                                value={newVehicle?.price_from}
                                prefix={'R'}
                              />
                            )}
                          </Heading>
                          <Text
                            color={'white'}
                            alignSelf={'flex-end'}
                            marginBottom={1}
                          >
                            Incl VAT,
                          </Text>
                        </Flex>
                        <Flex
                          gap={{ base: '2' }}
                          marginBottom={6}
                          justifyContent={'center'}
                        >
                          <Text
                            color={'white'}
                            alignSelf={'flex-end'}
                            marginBottom={1}
                          >
                            or as little as
                          </Text>
                          <Heading
                            as="h2"
                            className={Styles.titleMd}
                            fontSize={{ base: '2xl', sm: '4xl' }}
                            color={'white'}
                          >
                            <PriceFormat
                              value={newVehicle?.price_per_month_from}
                              prefix="R"
                            />
                          </Heading>
                          <Text
                            color={'white'}
                            alignSelf={'flex-end'}
                            marginBottom={1}
                          >
                            per month *
                          </Text>
                        </Flex>
                      </>
                    ) : newVehicle?.price_display_mode ===
                        PriceDisplayModes.RETAIL && newVehicle?.price_from ? (
                      <>
                        <Flex
                          gap={{ base: '2' }}
                          marginBottom={6}
                          justifyContent={'center'}
                        >
                          <Text color={'white'} marginTop={'6px'}>
                            From
                          </Text>
                          <Heading
                            as="h2"
                            className={Styles.titleMd}
                            fontSize={{ base: '2xl', sm: '4xl' }}
                            color={'white'}
                          >
                            {newVehicle?.price_from !== undefined && (
                              <PriceFormat
                                value={newVehicle?.price_from}
                                prefix={'R'}
                              />
                            )}
                          </Heading>
                          <Text
                            color={'white'}
                            alignSelf={'flex-end'}
                            marginBottom={1}
                          >
                            Incl VAT
                          </Text>
                        </Flex>
                      </>
                    ) : newVehicle?.price_per_month_from ? (
                      <>
                        <Flex
                          gap={{ base: '2' }}
                          marginBottom={6}
                          justifyContent={'center'}
                        >
                          <Text color={'white'} marginTop={'6px'}>
                            From
                          </Text>
                          <Heading
                            as="h2"
                            className={Styles.titleMd}
                            fontSize={{ base: '2xl', sm: '4xl' }}
                            color={'white'}
                          >
                            {newVehicle?.price_per_month_from !== undefined && (
                              <PriceFormat
                                value={newVehicle?.price_per_month_from}
                                prefix={'R'}
                              />
                            )}
                          </Heading>
                          <Text
                            color={'white'}
                            alignSelf={'flex-end'}
                            marginBottom={1}
                          >
                            per month *
                          </Text>
                        </Flex>
                      </>
                    ) : null}

                  {banner?.key_features?.length &&
                    align === 'centre_bottom' ? (
                      <HStack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        gap={10}
                        marginTop={'auto'}
                        marginBottom={12}
                      >
                        {banner?.key_features &&
                          banner?.key_features.map((feature, index) => (
                            <React.Fragment key={index}>
                              <Box
                                key={index}
                                textAlign={'center'}
                                color={'white'}
                              >
                                <Text as="b" fontSize="2xl">
                                  {feature?.text}
                                </Text>
                              </Box>
                              {banner?.key_features &&
                                index !== banner?.key_features.length - 1 && (
                                  <Divider
                                    orientation="vertical"
                                    borderColor={'gray.300'}
                                    h={{ base: '80px', sm: '60px', md: '58px' }}
                                  />
                                )}
                            </React.Fragment>
                          ))}
                      </HStack>
                    ) : null}

                    <Flex gap={4} className={Styles.bannerButtons}>
                      {banner?.show_book_test_drive && (
                        <ButtonCustom
                          btnText={banner?.book_test_drive_label || 'Book a Test Drive'}
                          className="btnWhite"
                          moduleClass={Styles.bannerCta}
                          onClick={() =>
                            openLeadForm('test-drive', 'TestDrive', {
                              type: 'new-car',
                              newCar: newVehicle,
                            })
                          }
                        />
                      )}
                      {banner?.source_code && (
                        <ButtonCustom
                          btnText="Apply for Finance"
                          className="btnWhite"
                          moduleClass={Styles.bannerCta}
                          onClick={() =>
                            openLeadForm('finance', 'finance', {
                              type: 'new-car',
                              newCar: newVehicle,
                            })
                          }
                        />
                      )}
                      {banner?.show_call_to_action &&
                        banner?.call_to_action_text &&
                        banner?.call_to_action_url && (
                          <LinkCustom
                            href={banner?.call_to_action_url}
                            className="btnOutlineWhite"
                            moduleClass={Styles.bannerCta}
                          >
                            {banner?.call_to_action_text}
                          </LinkCustom>
                        )}
                      {banner?.show_call_to_action_2 &&
                        banner?.call_to_action_text_2 &&
                        banner?.call_to_action_url_2 && (
                          <LinkCustom
                            href={banner?.call_to_action_url_2 ?? '#'}
                            className="btnOutlineWhite"
                            moduleClass={Styles.bannerCta}
                          >
                            {banner?.call_to_action_text_2}
                          </LinkCustom>
                        )}
                    </Flex>

                    <Box
                      textAlign={'center'}
                      color={'white'}
                      marginTop={'10px'}
                    >
                      <Text>{banner?.terms_and_conditions}</Text>
                    </Box>
                  </Box>

                  {video && (
                    <Box className={Styles.videoPlayerController}>
                      <Stack gap={2.5} marginEnd={2}>
                        {playing ? (
                          <Flex
                            alignItems={'center'}
                            onClick={() => videoHandler('pause')}
                            cursor={'pointer'}
                          >
                            <Image
                              alt="play"
                              title="play"
                              height={16}
                              width={16}
                              src={PlayIconDisable}
                            />
                            <Image
                              alt="pause"
                              title="pause"
                              height={16}
                              width={16}
                              src={PauseIcon}
                            />
                            <Text
                              as="b"
                              lineHeight={'1'}
                              mt={0.5}
                              fontSize="xs"
                              color={'white'}
                            >
                              PAUSE
                            </Text>
                          </Flex>
                        ) : (
                          <Flex
                            alignItems={'center'}
                            onClick={() => videoHandler('play')}
                            cursor={'pointer'}
                          >
                            <Image
                              alt="play"
                              title="play"
                              height={16}
                              width={16}
                              src={PlayIcon}
                            />
                            <Image
                              alt="pause"
                              title="pause"
                              height={16}
                              width={16}
                              src={PauseIconDisable}
                            />
                            <Text
                              as="b"
                              lineHeight={'1'}
                              mt={0.5}
                              fontSize="xs"
                              color={'white'}
                            >
                              PLAY
                            </Text>
                          </Flex>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Container>
            </Box>

            <Box className={Styles.slideMobile}>
              {banner?.key_features?.length && align === 'centre_bottom' && (
                <Stack
                  alignItems={'center'}
                  justifyContent={'center'}
                  gap={4}
                  padding={14}
                  backgroundColor={'#f8f8f8'}
                >
                  {banner?.key_features &&
                    banner?.key_features.map((feature, index) => (
                      <div key={index}>
                        <Box key={index} textAlign={'center'} color={'white'}>
                          <Text as="b" fontSize="2xl" color="gray.600">
                            {feature?.text}
                          </Text>
                        </Box>
                        {banner?.key_features &&
                          index !== banner?.key_features.length - 1 && (
                            <Divider
                              orientation="horizontal"
                              borderColor={'gray.600'}
                            />
                          )}
                      </div>
                    ))}
                </Stack>
              )}
            </Box>
          </Box>
      </div>
    </Box>
  );
};

export default BannerSection;